import type { WorkerProfile } from "@/types/userTypes";
import { Avatar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { encodeId } from "@/utils/idEncoder";
import { workerPath } from "@/shared/publicPaths";
import { useMemo } from "react";

type Props = {
  candidate: WorkerProfile;
  isSelected?: boolean;
  selectionDisabled?: boolean;
  selectionMode?: "auto" | "manual";
  onToggleSelected?: (candidate: WorkerProfile) => void;
  /** desktop = grid card from request-quote-form; mobile = choose-professionals list card */
  layout?: "desktop" | "mobile";
};

const slugifyName = (first?: string, last?: string) => {
  const fullName = `${first || ""} ${last || ""}`.trim();
  return fullName
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

const initials = (first?: string, last?: string) => {
  const a = (first || "").trim().charAt(0);
  const b = (last || "").trim().charAt(0);
  return `${a}${b}`.toUpperCase() || "?";
};

export function QuoteProviderCard({
  candidate,
  isSelected = false,
  selectionDisabled = false,
  selectionMode = "manual",
  onToggleSelected,
  layout = "desktop",
}: Props) {
  const { t } = useTranslation();

  const profilePath = useMemo(() => {
    const shortId = encodeId(candidate?.id || candidate?._id);
    const slug = `${slugifyName(candidate?.user?.first_name, candidate?.user?.last_name)}-${shortId}`;
    return workerPath(slug);
  }, [
    candidate?.id,
    candidate?._id,
    candidate?.user?.first_name,
    candidate?.user?.last_name,
  ]);

  const name =
    `${candidate.user?.first_name || ""} ${candidate.user?.last_name || ""}`.trim() ||
    t("professionals");

  const rating = candidate?.averageRating
    ? Number(candidate.averageRating).toFixed(1)
    : null;

  const services = (candidate.servicesOffered || []).slice(0, 3);
  const extraCount = Math.max(
    0,
    (candidate.servicesOffered?.length || 0) - services.length
  );

  const rateLabel = candidate.hourlyPay
    ? `$${candidate.hourlyPay}/${t("hourly")}`
    : "—";

  const locationLabel = candidate.locationName || t("remote");

  const canSelect = selectionMode === "manual";

  const handleSelectClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectionDisabled && !isSelected) return;
    onToggleSelected?.(candidate);
  };

  const selectButton = canSelect ? (
    <button
      type="button"
      disabled={selectionDisabled && !isSelected}
      onClick={handleSelectClick}
      className={`h-10 rounded-lg px-6 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        isSelected
          ? "bg-[#4285F4] text-white"
          : layout === "desktop"
            ? "w-full border border-[#4285F4] bg-white text-[#4285F4] hover:bg-[#e7eeff]"
            : "w-[128px] bg-[#dee8ff] text-[#4285F4] hover:bg-[#d8e3fb]"
      }`}
    >
      {isSelected
        ? t("selected")
        : layout === "desktop"
          ? `+ ${t("select")}`
          : t("select")}
    </button>
  ) : null;

  const avatar = candidate.user?.avatar ? (
    <Avatar src={candidate.user.avatar} alt="" sx={{ width: 64, height: 64 }} />
  ) : (
    <div className="flex size-16 items-center justify-center rounded-full bg-[#d8e3fb] text-sm font-semibold text-[#4285F4]">
      {initials(candidate.user?.first_name, candidate.user?.last_name)}
    </div>
  );

  if (layout === "mobile") {
    return (
      <div
        className={`flex flex-col gap-3 rounded-xl bg-white p-4 shadow-sm border ${
          isSelected ? "border-[#4285F4]" : "border-transparent"
        }`}
      >
        {/* Card body → profile; Select stays separate so it does not navigate */}
        <a
          href={profilePath}
          className="flex gap-4 items-start text-inherit no-underline"
        >
          <div className="shrink-0">{avatar}</div>
          <div className="min-w-0 flex-1 flex flex-col gap-1">
            <p className="text-xl font-semibold text-[#111c2d] truncate">
              {name}
            </p>
            <p className="text-xs font-medium text-[#424654]">
              {rating ? `${rating} (${t("reviews")})` : t("noReviewsYet")}
            </p>
            <p className="text-sm text-[#424654] truncate">{locationLabel}</p>
          </div>
        </a>

        {services.length > 0 && (
          <a
            href={profilePath}
            className="flex flex-wrap gap-2 no-underline"
          >
            {services.map((service) => (
              <span
                key={service?._id}
                className="rounded-md bg-[#dce3f0] px-2 py-1 text-[10px] uppercase text-[#5e6570]"
              >
                {service?.name}
              </span>
            ))}
            {extraCount > 0 && (
              <span className="rounded-md bg-[#dce3f0] px-2 py-1 text-[10px] uppercase text-[#5e6570]">
                +{extraCount}
              </span>
            )}
          </a>
        )}

        <div className="flex items-center justify-between border-t border-[#e7eeff] pt-3">
          <a href={profilePath} className="no-underline text-inherit">
            <p className="text-xs font-medium text-[#424654]">
              {t("estRate")}
            </p>
            <p className="text-xl font-semibold text-[#111c2d]">{rateLabel}</p>
          </a>
          {selectButton}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full gap-4 rounded-xl bg-white p-4 shadow-sm border ${
        isSelected ? "border-[#4285F4]" : "border-[#e7eeff]"
      }`}
    >
      <a
        href={profilePath}
        className="flex gap-3 items-start text-inherit no-underline"
      >
        <div className="shrink-0">{avatar}</div>
        <div className="min-w-0 flex-1">
          <p className="block text-base font-semibold text-[#111c2d] truncate">
            {name}
          </p>
          <p className="text-sm text-[#424654] mt-0.5">
            {rating ? `${rating} (${t("reviews")})` : t("noReviewsYet")}
          </p>
          <p className="text-sm text-[#424654] truncate mt-0.5">
            {locationLabel}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-base font-semibold text-[#111c2d]">{rateLabel}</p>
          <p className="text-xs text-[#424654]">{t("estRate")}</p>
        </div>
      </a>

      {services.length > 0 && (
        <a href={profilePath} className="flex flex-wrap gap-2 no-underline">
          {services.map((service) => (
            <span
              key={service?._id}
              className="rounded-full bg-[#e7eeff] px-2.5 py-1 text-xs text-[#4285F4]"
            >
              {service?.name}
            </span>
          ))}
          {extraCount > 0 && (
            <span className="rounded-full bg-[#e7eeff] px-2.5 py-1 text-xs text-[#4285F4]">
              +{extraCount}
            </span>
          )}
        </a>
      )}

      {selectButton ? <div className="mt-auto pt-1">{selectButton}</div> : null}
    </div>
  );
}
